// Pure predicate for "does this issue offer a one-click CREATE of the
// policy the carrier sent us?" — Grant's ask, 2026-08-18: "i dont want
// her to click around all over. i want it to be easy to just click and
// create a policy for that IVANS data if need be."
//
// The ivans-unmatched-transaction issue says the carrier is servicing a
// policy the OS has no record of. Its recommended action is to create
// it — but a policy cannot exist without a household, and WHICH
// household is exactly the judgment the item exists for. So the
// recommendation can't complete on its own: the rail opens a picker,
// the same shape as the realty "pick the client household" flow
// (realty-link-predicate.ts).
//
// Deliberately an explicit slug + verb match rather than a shape
// heuristic — same convention as the realty and household-search
// predicates. An `activity`-kind issue recommending some other verb is
// not this flow.

export type RecordRef = { kind: string; id: string; label: string };

export interface IssueLike {
  detectorSlug?: string;
  affectedRecords?: RecordRef[];
  recommendation?: { verb: string; params?: Record<string, unknown> } | null;
}

export const IVANS_UNMATCHED_DETECTOR_SLUG = "ivans-unmatched-transaction";

/** The carrier-transaction subject the create-policy overlay drives, or
 *  null when this isn't that flow. */
export function ivansCreatePolicySource(issue: IssueLike): RecordRef | null {
  if (issue.detectorSlug !== IVANS_UNMATCHED_DETECTOR_SLUG) return null;
  if (issue.recommendation?.verb !== "create-policy") return null;
  return (issue.affectedRecords ?? []).find((r) => r.kind === "activity") ?? null;
}

/** The carrier's insured name, pre-split for the "new client" form.
 *  A convenience for the form only — never trusted as policy data (the
 *  server re-reads every figure from the carrier's own row). */
export function ivansSuggestedClientName(issue: IssueLike): {
  first: string;
  last: string;
} {
  const p = issue.recommendation?.params ?? {};
  return {
    first: typeof p.suggested_first_name === "string" ? p.suggested_first_name : "",
    last: typeof p.suggested_last_name === "string" ? p.suggested_last_name : "",
  };
}

/** The household the matcher already identified by address (and usually
 *  surname), pre-selected in the picker so the common case is
 *  confirm-and-go rather than search.
 *
 *  A SUGGESTION, never an authority: the server re-reads the household
 *  by whatever id the panel finally submits and re-checks Insurance
 *  scope on it, so a wrong suggestion costs a click, not a bad record.
 *  Returns null unless BOTH the id and a display name are present — a
 *  nameless pre-selection would put an unlabelled row in front of Joy. */
export function ivansSuggestedHousehold(issue: IssueLike): { id: string; name: string } | null {
  const p = issue.recommendation?.params ?? {};
  const id = typeof p.suggested_household_id === "string" ? p.suggested_household_id.trim() : "";
  const name =
    typeof p.suggested_household_name === "string" ? p.suggested_household_name.trim() : "";
  if (!id || !name) return null;
  return { id, name };
}

/** The policy type the carrier's line-of-business code maps to, or ""
 *  when the code has no unambiguous OS equivalent and the human must
 *  choose. */
export function ivansSuggestedPolicyType(issue: IssueLike): string {
  const p = issue.recommendation?.params ?? {};
  return typeof p.policy_type === "string" ? p.policy_type : "";
}
